import MDEditor from "@uiw/react-md-editor";
import {
  Box,
  Button,
  Chip,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useMutation, useQuery } from "react-query";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import Loader from "../../../components/Loader";
import { blogService } from "../../../api/services/blogService";

const BlogEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = Boolean(id);

  const [formData, setFormData] = useState({
    title: "",
    content: "",
    excerpt: "",
    featured_image: "",
    category: "",
    tags: "",
    is_featured: false,
    meta_title: "",
    meta_description: "",
    status: "DRAFT" as "DRAFT" | "PUBLISHED" | "ARCHIVED",
  });

  // Load existing article if editing
  const { data: article, isLoading } = useQuery(
    ["blog", id],
    () => blogService.getBlogById(Number(id)),
    {
      enabled: isEdit,
      onSuccess: (data) => {
        setFormData({
          title: data.data.title || "",
          content: data.data.content || "",
          excerpt: data.data.excerpt || "",
          featured_image: data.data.featured_image || "",
          category: data.data.category || "",
          tags: data.data.tags || "",
          is_featured: data.data.is_featured || false,
          meta_title: data.data.meta_title || "",
          meta_description: data.data.meta_description || "",
          status: data.data.status || "DRAFT",
        });
      },
    }
  );

  const saveMutation = useMutation(
    (data: any) =>
      isEdit
        ? blogService.updateBlog(Number(id), data)
        : blogService.createBlog(data),
    {
      onSuccess: () => {
        toast.success(isEdit ? "Article updated!" : "Article created!");
        navigate("/blog");
      },
      onError: () => {
        toast.error("Failed to save article");
      },
    }
  );

  const handleSave = (status?: "DRAFT" | "PUBLISHED") => {
    const dataToSave = {
      ...formData,
      status: status || formData.status,
    };
    saveMutation.mutate(dataToSave);
  };

  if (isLoading) {
    return <Loader />;
  }

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" sx={{ mb: 3 }}>
        {isEdit ? "Edit Article" : "Create New Article"}
      </Typography>

      <Grid container spacing={3}>
        {/* Title */}
        <Grid item xs={12}>
          <TextField
            label="Title *"
            placeholder="Enter article title"
            value={formData.title}
            onChange={(e) =>
              setFormData({ ...formData, title: e.target.value })
            }
            fullWidth
            required
          />
        </Grid>

        {/* Markdown Editor */}
        <Grid item xs={12}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Content * (Markdown)
          </Typography>
          <MDEditor
            value={formData.content}
            onChange={(val) => setFormData({ ...formData, content: val || "" })}
            height={500}
            preview="edit"
          />
        </Grid>

        {/* Excerpt */}
        <Grid item xs={12}>
          <TextField
            label="Excerpt"
            placeholder="Short summary for article listing"
            value={formData.excerpt}
            onChange={(e) =>
              setFormData({ ...formData, excerpt: e.target.value })
            }
            multiline
            rows={3}
            fullWidth
          />
        </Grid>

        {/* Featured Image */}
        <Grid item xs={12} md={6}>
          <TextField
            label="Featured Image URL"
            placeholder="https://example.com/image.jpg"
            value={formData.featured_image}
            onChange={(e) =>
              setFormData({ ...formData, featured_image: e.target.value })
            }
            fullWidth
          />
        </Grid>

        {/* Category */}
        <Grid item xs={12} md={6}>
          <TextField
            label="Category"
            placeholder="e.g., Technology, Health, News"
            value={formData.category}
            onChange={(e) =>
              setFormData({ ...formData, category: e.target.value })
            }
            fullWidth
          />
        </Grid>

        {/* Tags */}
        <Grid item xs={12}>
          <TextField
            label="Tags"
            placeholder="Comma-separated tags (e.g., react, typescript, tutorial)"
            value={formData.tags}
            onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
            fullWidth
            helperText="Separate tags with commas"
          />
        </Grid>

        {/* Status */}
        <Grid item xs={12} md={6}>
          <FormControl fullWidth>
            <InputLabel>Status</InputLabel>
            <Select
              value={formData.status}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  status: e.target.value as "DRAFT" | "PUBLISHED" | "ARCHIVED",
                })
              }
              label="Status"
            >
              <MenuItem value="DRAFT">Draft</MenuItem>
              <MenuItem value="PUBLISHED">Published</MenuItem>
              <MenuItem value="ARCHIVED">Archived</MenuItem>
            </Select>
          </FormControl>
        </Grid>

        {/* Featured Toggle */}
        <Grid item xs={12} md={6}>
          <FormControlLabel
            control={
              <Switch
                checked={formData.is_featured}
                onChange={(e) =>
                  setFormData({ ...formData, is_featured: e.target.checked })
                }
              />
            }
            label="Featured Article"
          />
        </Grid>

        {/* SEO Section */}
        <Grid item xs={12}>
          <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
            SEO Settings (Optional)
          </Typography>
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            label="SEO Meta Title"
            placeholder="Leave empty to use article title"
            value={formData.meta_title}
            onChange={(e) =>
              setFormData({ ...formData, meta_title: e.target.value })
            }
            fullWidth
          />
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            label="SEO Meta Description"
            placeholder="Leave empty to use excerpt"
            value={formData.meta_description}
            onChange={(e) =>
              setFormData({ ...formData, meta_description: e.target.value })
            }
            fullWidth
          />
        </Grid>

        {/* Action Buttons */}
        <Grid item xs={12}>
          <Box
            sx={{ display: "flex", gap: 2, justifyContent: "flex-end", mt: 2 }}
          >
            <Button variant="outlined" onClick={() => navigate("/blog")}>
              Cancel
            </Button>
            <Button variant="outlined" onClick={() => handleSave("DRAFT")}>
              Save as Draft
            </Button>
            <Button
              variant="contained"
              onClick={() => handleSave("PUBLISHED")}
              disabled={!formData.title || !formData.content}
            >
              {isEdit ? "Update & Publish" : "Publish"}
            </Button>
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default BlogEditor;
